/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { HeaderAccount } from '@/components/content/Header/parts/Account';
import { HeaderMiniCart } from '@/components/content/Header/parts/MiniCart';
import { HeaderMobileDrawer } from '@/components/content/Header/parts/MobileDrawer';
import { HeaderNavBar } from '@/components/content/Header/parts/NavBar';
import { HeaderSearch } from '@/components/content/Header/parts/Search';
import { HeaderSearch as HelloRetailSearch } from '@/components/content/Header/parts/HelloRetail/HelloRetailSearch';
import {
	headerContainerSX,
	headerLogoSearchStackSX,
} from '@/components/content/Header/styles/container';
import { ID } from '@/data/types/Basic';
import { Box, Container, Paper, Stack } from '@mui/material';
import { FC, useMemo } from 'react';
import { CategoryLinksSEO } from './parts/CategoryLinksSEO';
import { HeaderUspBar } from './parts/HeaderUspBar';
import { WishlistIcon } from './parts/WishlistIcon';
import { MergeCartConfirmationDialog } from '@/components/content/MergeCartConfirmationDialog';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { homeStructuredData } from '@/data/constants/structuredData';
import { useSettings } from '@/data/Settings';
import { PRE_CONNECT_ALL, PRE_CONNECT_HOME, PRE_CONNECT_STLOC } from '@/data/constants/preConnect';
import NextHead from 'next/head';
import { useDeviceType } from '@/data/deviceDetection';
import { SkipToMainContent } from '@/components/blocks/SkipToMainContent';
import { CookiebotDeclaration } from '@/components/content/Header/parts/CookiebotDeclaration';

export const Header: FC<{
	id: ID;
}> = ({ id }) => {
	const { data: pageData } = usePageDataFromId();
	const { settings } = useSettings();
	const { isMobile, isTablet } = useDeviceType();
	const isStoreLocator = useMemo<boolean>(
		() => pageData?.page?.type === 'StoreLocatorPage',
		[pageData?.page?.type]
	);

	const isHome = useMemo<boolean>(
		() =>
			(
				settings?.userData?.HomePageTokenValue &&
				pageData?.tokenExternalValue === settings?.userData?.HomePageTokenValue
			) === true,
		[pageData?.tokenExternalValue, settings?.userData?.HomePageTokenValue]
	);

	const isHelloRetailSearchEnabled = settings?.userData?.HelloRetailSearchEnabled === "true";

	const structuredData = useMemo(() => (isHome ? homeStructuredData : null), [isHome]);

	const preconnectUrls = useMemo(
		() =>
			[
				...PRE_CONNECT_ALL,
				...(isHome ? PRE_CONNECT_HOME : []),
				...(isStoreLocator ? PRE_CONNECT_STLOC : []),
			].filter((value, index, arr) => arr.indexOf(value) === index),
		[isHome, isStoreLocator]
	);

	return (
		<>
			<CategoryLinksSEO />
			{structuredData ? <script type="application/ld+json" dangerouslySetInnerHTML={{__html: structuredData}}></script> : null}
			<Box id="page_top"></Box>
			{/* Placeholder for Scrollup widget */}
			<Paper className="header" component="header" sx={headerContainerSX} elevation={0}>
			<SkipToMainContent />
			<HeaderUspBar {...{ isMobile }} />
				<Container>
					{preconnectUrls.length ? (
						<NextHead>
							{preconnectUrls.map((url) => (
								<link key={url} rel="preconnect" href={url} />
							))}
						</NextHead>
					) : null}

					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={2}
						py={2}
					>
						<Stack direction="row" alignItems="center" spacing={2} sx={headerLogoSearchStackSX}>
							{isMobile ? <HeaderMobileDrawer /> : null} {/*  TBD : To be called only on Mobile render.  */}
							<Box className="header-store-logo">
								<ContentRecommendation
									key={'HeaderStoreLogo'}
									id={`${id}-HeaderStoreLogo`}
									properties={{ emsName: 'HeaderStoreLogo' }}
								/>
							</Box>
							<Stack ml={'0px !important'}>
								{/* tablet of desktop device location   */}
								{(!isMobile || isTablet )?    
									isHelloRetailSearchEnabled ? <HelloRetailSearch  mobile={false || isTablet}/> : <HeaderSearch mobile={false} /> 
								: null}
							</Stack>
						</Stack>
						<Stack 
							direction="row" 
							alignItems="center" 
							justifyContent="flex-end" 
							spacing={2} 
							sx={{
								'& > div':{ml:'0px !important'}}} 
								columnGap={isMobile ? 2 : isTablet ? 3 : 6}
						>
							<WishlistIcon />
							{/* <HeaderLocateStore /> */}
							<HeaderAccount mobileBreakpoint="md" />
							{/* <Typography fontSize={30}>|</Typography> */}
							<HeaderMiniCart mobileBreakpoint="md" />
							{/* <HeaderLanguage /> -- functionality not needed */}
						</Stack>
					</Stack>
				</Container>
				 
				{/* mobile device location   */}
				{(isMobile && !isTablet) ? 
					isHelloRetailSearchEnabled ? <HelloRetailSearch mobile={true && !isTablet} /> : <HeaderSearch mobile={true} /> 
				: null}
				{!isMobile || isTablet ? <HeaderNavBar /> : null}
				<MergeCartConfirmationDialog />
				<CookiebotDeclaration />
			</Paper>
		</>
	);
};
